import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  CLASSIFICATION_OPTIONS,
  PRIORITY_OPTIONS,
  REGULATORY_AUTHORITY_OPTIONS,
  SOURCE_OPTIONS
} from '../adsb/constants/adsbConstants';
import { BULLETIN_FILTER_LABELS } from '../maintenance/utils/maintenanceUtils';
import {
  downloadAdsbReportHistoryCsv,
  downloadAdsbReportHistoryPdf,
  downloadAdsbReportCsv,
  downloadAdsbReportPdf,
  fetchAdsbReportPreviewData,
  fetchAdsbReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const STATUS_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFilterSummary(labels, fallback = 'All') {
  return labels?.length ? labels.join(', ') : fallback;
}

function formatAssemblyLabel(asm) {
  if (!asm) return '';
  const typeName = asm.typeName || 'Assembly';
  const sn = asm.serialNumber || 'N/A';
  const reg = asm.aircraftId?.registration;
  const base = `${typeName} – SN: ${sn}`;
  return reg ? `${base} (${reg})` : base;
}

/**
 * Dedicated AD/SB Report flow aligned with the AD/SB Service Bulletin page.
 */
const AdsbReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [selectedAssemblyIds, setSelectedAssemblyIds] = useState([]);
  const [selectedClassifications, setSelectedClassifications] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedRegulatoryAuthorities, setSelectedRegulatoryAuthorities] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [bulletinTypeFilter, setBulletinTypeFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [assemblyOptions, setAssemblyOptions] = useState([]);
  const [assemblyLoading, setAssemblyLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const assemblyLabelById = useMemo(() => {
    const map = {};
    assemblyOptions.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [assemblyOptions]);

  const loadAssemblies = useCallback(async (aircraftIds) => {
    setAssemblyLoading(true);
    try {
      const params = { isActive: 'true' };
      if (aircraftIds.length === 1) {
        params.aircraftId = aircraftIds[0];
      }
      const response = await axios.get('/api/assemblies', { params });
      const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      list.sort((a, b) => {
        const la = `${a.typeName || ''} ${a.serialNumber || ''}`.toLowerCase();
        const lb = `${b.typeName || ''} ${b.serialNumber || ''}`.toLowerCase();
        return la.localeCompare(lb);
      });
      const options = list.map((asm) => ({
        value: asm._id,
        label: formatAssemblyLabel(asm)
      }));
      setAssemblyOptions(options);
      setSelectedAssemblyIds((prev) => prev.filter((id) => options.some((opt) => opt.value === id)));
    } catch (err) {
      console.error('Failed to load assemblies for AD/SB report:', err);
      setAssemblyOptions([]);
      setSelectedAssemblyIds([]);
    } finally {
      setAssemblyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssemblies(selectedAircraftIds);
  }, [selectedAircraftIds, loadAssemblies]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchAdsbReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const fileStamp = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : reportDate;

  const reportPayload = useMemo(() => ({
    aircraftIds: selectedAircraftIds,
    assemblyIds: selectedAssemblyIds,
    classifications: selectedClassifications,
    priorities: selectedPriorities,
    sources: selectedSources,
    regulatoryAuthorities: selectedRegulatoryAuthorities,
    statusFilter,
    bulletinTypeFilter,
    search,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate
  }), [
    selectedAircraftIds,
    selectedAssemblyIds,
    selectedClassifications,
    selectedPriorities,
    selectedSources,
    selectedRegulatoryAuthorities,
    statusFilter,
    bulletinTypeFilter,
    search,
    dateFrom,
    dateTo,
    reportDate
  ]);

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchAdsbReportPreviewData(reportPayload);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchAdsbReportPreviewData(reportPayload);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadAdsbReportPdf(
        { ...reportPayload, visualizations },
        `ADSB_Report_${fileStamp}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'AD/SB Report with visualizations downloaded and saved to history'
          : 'AD/SB Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) {
        loadHistory();
      }
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadAdsbReportCsv(reportPayload, `ADSB_Report_${fileStamp}.pdf`);
      showToast('AD/SB Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryDownload = (row) => {
    try {
      downloadAdsbReportHistoryPdf(row._id, row.filename || 'ADSB_Report.pdf');
      showToast('Re-downloading stored PDF', 'success');
    } catch (err) {
      showToast(err.message || 'Re-download failed', 'error');
    }
  };

  const handleHistoryDownloadCsv = (row) => {
    try {
      downloadAdsbReportHistoryCsv(row._id, row.filename || 'ADSB_Report.pdf');
      showToast('Downloading CSV', 'success');
    } catch (err) {
      showToast(err.message || 'CSV download failed', 'error');
    }
  };

  const busy = previewing || downloading || downloadingCsv || visualizing;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        AD/SB Report
      </Typography>
      <Typography color="text.secondary" paragraph>
        Airworthiness directives and service bulletins with the same filter dimensions as the AD/SB
        Service Bulletin page — aircraft, assembly, classification, source, priority, regulatory authority,
        open/closed status, bulletin mode, and search. Leave filters empty to include all matching bulletins.
        Each PDF download is stored so you can re-download it later.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="AD/SB number, title, description, serial numbers"
              helperText="Matches AD/SB #, title, description, and serial numbers / make model"
            />

            <FormControl fullWidth>
              <InputLabel id="adsb-report-aircrafts-label">Aircrafts</InputLabel>
              <Select
                labelId="adsb-report-aircrafts-label"
                multiple
                value={selectedAircraftIds}
                label="Aircrafts"
                onChange={(e) => {
                  const value = e.target.value;
                  const next = typeof value === 'string' ? value.split(',') : value;
                  setSelectedAircraftIds(next);
                  setSelectedAssemblyIds([]);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All aircraft';
                  return selected.map((id) => aircraftLabelById[id] || id).join(', ');
                }}
              >
                {aircraftOptions.map((ac) => (
                  <MenuItem key={ac.value} value={ac.value}>
                    {ac.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Optional — leave empty for all aircraft
              </Typography>
            </FormControl>

            <FormControl fullWidth disabled={assemblyLoading}>
              <InputLabel id="adsb-report-assemblies-label">Assemblies</InputLabel>
              <Select
                labelId="adsb-report-assemblies-label"
                multiple
                value={selectedAssemblyIds}
                label="Assemblies"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedAssemblyIds(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All assemblies';
                  return selected.map((id) => assemblyLabelById[id] || id).join(', ');
                }}
              >
                {assemblyOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {assemblyLoading
                  ? 'Loading assemblies…'
                  : selectedAircraftIds.length === 1
                    ? 'Filtered to assemblies on the selected aircraft'
                    : 'Optional — scoped to a single aircraft when one is selected'}
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="adsb-report-classifications-label">Classifications</InputLabel>
              <Select
                labelId="adsb-report-classifications-label"
                multiple
                value={selectedClassifications}
                label="Classifications"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedClassifications(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All classifications';
                  return selected
                    .map((value) => CLASSIFICATION_OPTIONS.find((opt) => opt.value === value)?.label || value)
                    .join(', ');
                }}
              >
                {CLASSIFICATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="adsb-report-priorities-label">Priorities</InputLabel>
              <Select
                labelId="adsb-report-priorities-label"
                multiple
                value={selectedPriorities}
                label="Priorities"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedPriorities(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All priorities';
                  return selected
                    .map((value) => PRIORITY_OPTIONS.find((opt) => opt.value === value)?.label || value)
                    .join(', ');
                }}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="adsb-report-sources-label">Sources</InputLabel>
              <Select
                labelId="adsb-report-sources-label"
                multiple
                value={selectedSources}
                label="Sources"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedSources(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All sources';
                  return selected
                    .map((value) => SOURCE_OPTIONS.find((opt) => opt.value === value)?.label || value)
                    .join(', ');
                }}
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="adsb-report-regulatory-label">Regulatory authorities</InputLabel>
              <Select
                labelId="adsb-report-regulatory-label"
                multiple
                value={selectedRegulatoryAuthorities}
                label="Regulatory authorities"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRegulatoryAuthorities(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'All authorities';
                  return selected
                    .map((value) => REGULATORY_AUTHORITY_OPTIONS.find((opt) => opt.value === value)?.label || value)
                    .join(', ');
                }}
              >
                {REGULATORY_AUTHORITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="adsb-report-status-label">Status</InputLabel>
              <Select
                labelId="adsb-report-status-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="adsb-report-bulletin-filter-label">Bulletin filter</InputLabel>
              <Select
                labelId="adsb-report-bulletin-filter-label"
                value={bulletinTypeFilter}
                label="Bulletin filter"
                onChange={(e) => setBulletinTypeFilter(e.target.value)}
                renderValue={(value) => BULLETIN_FILTER_LABELS[value] || value}
              >
                {Object.entries(BULLETIN_FILTER_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Controls one-time, not applicable, and recurring bulletin inclusion
              </Typography>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
            </Stack>

            {dateRangeError && (
              <Alert severity="warning">{dateRangeError}</Alert>
            )}

            <TextField
              label="Report date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Shown as the report date on the Prepared By section"
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />}
                onClick={handleVisualize}
                disabled={busy || Boolean(dateRangeError)}
              >
                Visualize
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <Preview />}
                onClick={handlePreview}
                disabled={busy || Boolean(dateRangeError)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={() => handleDownload()}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadCsv}
                disabled={busy || Boolean(dateRangeError)}
              >
                Download CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded AD/SB Reports</Typography>
            <Button
              size="small"
              startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={loadHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>

          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">
              No AD/SB Report downloads yet. Generate one from the Generate tab.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Search</TableCell>
                  <TableCell>Aircrafts</TableCell>
                  <TableCell>Assemblies</TableCell>
                  <TableCell>Classifications</TableCell>
                  <TableCell>Priorities</TableCell>
                  <TableCell>Sources</TableCell>
                  <TableCell>Authorities</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Bulletin filter</TableCell>
                  <TableCell align="right">Bulletins</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : '—'}
                    </TableCell>
                    <TableCell>{row.search || '—'}</TableCell>
                    <TableCell>
                      {(row.aircraftLabels || []).length
                        ? row.aircraftLabels.join(', ')
                        : 'All aircraft'}
                    </TableCell>
                    <TableCell>
                      {formatFilterSummary(row.assemblyLabels)}
                    </TableCell>
                    <TableCell>
                      {formatFilterSummary(row.classificationLabels)}
                    </TableCell>
                    <TableCell>
                      {formatFilterSummary(row.priorityLabels)}
                    </TableCell>
                    <TableCell>
                      {formatFilterSummary(row.sourceLabels)}
                    </TableCell>
                    <TableCell>
                      {formatFilterSummary(row.regulatoryAuthorityLabels)}
                    </TableCell>
                    <TableCell>
                      {row.statusFilterLabel || 'All'}
                    </TableCell>
                    <TableCell>
                      {row.bulletinTypeFilterLabel || BULLETIN_FILTER_LABELS[row.bulletinTypeFilter] || 'All'}
                    </TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownload(row)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownloadCsv(row)}
                        >
                          CSV
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="AD/SB Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
        onDownload={() => handleDownload()}
        downloading={downloading}
        onDownloadCsv={handleDownloadCsv}
        downloadingCsv={downloadingCsv}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="AD/SB Report"
        data={visualizeData}
        downloading={downloading}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdfWithVisualization={(visualizations) => handleDownload(visualizations)}
      />
    </Box>
  );
};

export default AdsbReportPanel;
